import * as React from 'react';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import { useState, useEffect } from "react";
import { useNavigate } from 'react-router-dom';
import { alterarEmail, auth } from '../../../contexts/authentication';
import { UserData, getUser } from '../../pages/getUserDB';
import { fetchSignInMethodsForEmail, signInWithEmailAndPassword, signOut, updateProfile } from 'firebase/auth';
import WarningAlert from '../WarningAlert';
import { isValidEmail } from '../../pages/profile/validationUserDates';
import EmailInput from '../EmailInput';
import PasswordInput from '../PasswordInput';
import { Typography } from '@mui/material';
import { useNewEmail } from '../../../frontend/globalStates/NewEmail';

interface ChangeEmailProps {
  showEmailModal: boolean;
  setShowEmailModal: React.Dispatch<React.SetStateAction<boolean>>;
  setShowAlertWithDone: React.Dispatch<React.SetStateAction<boolean>>;
}

export default function ChangeEmail({
  showEmailModal,
  setShowEmailModal,
  setShowAlertWithDone,
}: ChangeEmailProps) {
  const currentUser = auth.currentUser;
  const { newEmail, setNewEmail } = useNewEmail();
  const [currentPassword, setCurrentPassword] = useState('');
  const [showWarningAlert, setShowWarningAlert] = useState(false);
  const [warningMessage, setWarningMessage] = useState('');

  useEffect(() => {    
    if (showWarningAlert) {
      setTimeout(() => {
        setShowWarningAlert(false)
      }, 6000)
    }
  }, [showWarningAlert]);

  const handleEmailClick = async () => {
    if (!currentPassword) {
      setShowWarningAlert(true);
      setWarningMessage("Digite sua senha atual.");
      return;
    }

    if (currentUser?.email === newEmail) {
      setShowWarningAlert(true);
      setWarningMessage("O e-mail digitado deve ser diferente do email atual.");
      return;
    }

    if (!isValidEmail(newEmail)) {
      setShowWarningAlert(true);
      setWarningMessage("O e-mail informado não é válido.");
      return;
    }

    try {
      const signInMethods = await fetchSignInMethodsForEmail(auth, newEmail);

      if (signInMethods.length > 0) {

        setShowWarningAlert(true);
        setWarningMessage("O e-mail informado já está cadastrado.");
        return;
      }

      await alterarEmail(newEmail, currentPassword);
      setCurrentPassword('');
      setShowEmailModal(false);
      setShowAlertWithDone(true);
    } catch (error: any) {
      if (error.message.includes('Senha incorreta')) {
        setShowWarningAlert(true);
        setWarningMessage("Senha incorreta. Por favor, verifique e tente novamente.");
      } else {
        console.error("Erro ao alterar o email:", error);
        throw error;
      }
    }
  };

  return (
    <Dialog
      open={showEmailModal}
      onClose={() => setShowEmailModal(false)}
      slotProps={{
        paper: {
          component: 'form',
          onSubmit: (event: React.FormEvent<HTMLFormElement>) => {
            event.preventDefault();
            handleEmailClick();
          },
          sx: { backgroundImage: 'none', width: '100%' },
        },
      }}
    >
      <DialogTitle>Alterar email</DialogTitle>
      <DialogContent
        sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
      >
        <DialogContentText>
          Digite seu novo email.
        </DialogContentText>
        <Box
          sx={{ '& > :not(style)': { m: 1, width: '30ch' } }}
        >
          <EmailInput email={newEmail} setEmail={setNewEmail} />
        </Box>
        <Box
          sx={{ '& > :not(style)': { ml: 1, width: '30ch' }, display: 'flex', flexDirection: 'column' }}
        >
          <PasswordInput
            password={currentPassword}
            setPassword={setCurrentPassword}
            labelMessage="Senha atual"
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ pb: 3, px: 3 }}>
        <Button onClick={() => setShowEmailModal(false)}>Cancelar</Button>
        <Button variant="contained" type="submit">
          Continuar
        </Button>
      </DialogActions>
      {showWarningAlert && (
        <WarningAlert
          message={warningMessage}
        />
      )}
    </Dialog>
  );
}